import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { toast } from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";
import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  Camera,
  FileText,
  Droplet,
  Filter,
  Thermometer,
  CalendarDays,
  Cloud,
  Package,
} from "lucide-react";

const SectionCWaterSupply = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    c3Photo: null,
    c4Report: null,
  });

  const yesNoNaToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    if (value === "NA") return 3;
    return null;
  };

  const options = [t.global.yes, t.global.no, "NA"];

  const [form, setForm] = useState({
    c1: "",
    c2: "",
    c3: "",
    c3Date: "",
    c3Photo: null,
    c4: "",
    c4Date: "",
    c4Report: null,
    remarks: "",
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_c");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_c", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      // ✅ Save file in form
      setForm((prev) => ({ ...prev, [name]: file }));

      // ✅ Generate preview ONLY if image
      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);

        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.c1) newErrors.c1 = t.global.err_a1;
    if (!form.c2) newErrors.c2 = t.global.err_a1;
    if (!form.c3) newErrors.c3 = t.global.err_a1;
    if (!form.c4) newErrors.c4 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.c3Photo && form.c3Photo.size > 5 * 1024 * 1024) {
      newErrors.c3Photo = t.global.err_file_size;
    }
    if (form.c3Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.c3Photo.type)) {
        newErrors.c3Photo = t.global.err_file_type_long;
      }
    }

    if (form.c4Report && form.c4Report.size > 5 * 1024 * 1024) {
      newErrors.c4Report = t.global.err_file_size;
    }
    if (form.c4Report) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.c4Report.type)) {
        newErrors.c4Report = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            (error) => {
              console.warn("Geolocation error:", error);
              resolve(); // still proceed without location
            },
            { timeout: 10000, enableHighAccuracy: true },
          );
        });
      }

      // Create FormData for API
      const fd = new FormData();
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);
      // Section C data
      fd.append("water_supply_C1", yesNoNaToInt(form.c1));
      fd.append("water_supply_C2", yesNoNaToInt(form.c2));

      fd.append("water_supply_C3", yesNoNaToInt(form.c3));
      if (form.c3 === t.global.yes && form.c3Date) {
        fd.append("water_supply_C3_date", form.c3Date);
      }
      if (form.c3 === t.global.yes && form.c3Photo) {
        fd.append("water_supply_C3_upload", form.c3Photo);
      }

      fd.append("water_supply_C4", yesNoNaToInt(form.c4));
      if (form.c4 === t.global.yes && form.c4Date) {
        fd.append("water_supply_C4_date", form.c4Date);
      }
      if (form.c4 === t.global.yes && form.c4Report) {
        fd.append("water_supply_C4_upload", form.c4Report);
      }

      fd.append("current_section", "water");

      // Dispatch the action
      await dispatch(createKitchenDining(fd));

      // ✅ Check if this section is partial (missing optional uploads/dates when YES is selected)
      const isPartial =
        (form.c3 === t.global.yes && (!form.c3Date || !form.c3Photo)) ||
        (form.c4 === t.global.yes && (!form.c4Date || !form.c4Report));

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_c", JSON.stringify(sectionData));

      // ✅ Mark Section C as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.c = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // Save locally for offline
      const isPartial =
        (form.c3 === t.global.yes && (!form.c3Date || !form.c3Photo)) ||
        (form.c4 === t.global.yes && (!form.c4Date || !form.c4Report));

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_c", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.c = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                {t.global.success_message}
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchenCumDining.sections.C.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchenCumDining.sections.C.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
                <Droplet size={14} className="mr-1" />
                {t.modules.kitchenCumDining.sections.C.title}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.area_id}: ${state.area_name}` : t.global.na}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* C1 - Water Storage Capacity */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Cloud size={18} className="mr-2" />
                C1. {t.modules.kitchenCumDining.sections.C.questions.storage}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchenCumDining.sections.C.hints.storage.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Minimum 45 liters per person per day</li>
                  <li>Storage for 2 days supply</li>
                  <li>Adequate for cooking, cleaning, and drinking</li>
                  <li>Covered and clean storage containers</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {options.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.c1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.c1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="c1"
                      value={option}
                      checked={form.c1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.c1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c1}
                </p>
              )}
            </div>

            {/* C2 - Water Filter */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Filter size={18} className="mr-2" />
                C2. {t.modules.kitchenCumDining.sections.C.questions.filter}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.c2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.c2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="c2"
                      value={option}
                      checked={form.c2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.c2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c2}
                </p>
              )}
            </div>

            {/* C3 - Tank Cleaning */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Droplet size={18} className="mr-2" />
                C3. {t.modules.kitchenCumDining.sections.C.questions.tankCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchenCumDining.sections.C.hints.tankCleaning}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {options.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.c3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.c3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="c3"
                      value={option}
                      checked={form.c3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.c3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c3}
                </p>
              )}

              {form.c3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DATE INPUT */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 flex items-center">
                      <CalendarDays size={16} className="mr-2" />
                      Last date of tank cleaning:
                    </label>

                    <input
                      type="date"
                      name="c3Date"
                      value={form.c3Date}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      max={new Date().toISOString().split("T")[0]}
                      className={`w-full border p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                        errors.c3Date ? "border-red-500" : "border-gray-300"
                      }`}
                    />

                    {errors.c3Date && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c3Date}
                      </p>
                    )}

                    <p className="text-xs text-gray-500 mt-1">
                      If not entered, section will be marked as "Partially Filled"
                    </p>
                  </div>

                  {/* PHOTO SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Photo of tank cleaning (Optional):
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c3Photo");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c3Photo"
                        className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                          isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    {/* HIDDEN FILE INPUT */}
                    <input
                      type="file"
                      name="c3Photo"
                      id="c3Photo"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW BOX */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                        form.c3Photo
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c3Photo ? (
                        <div className="space-y-4">
                          {/* ✅ IMAGE PREVIEW */}
                          {previewImages.c3Photo &&
                            form.c3Photo.type.startsWith("image/") && (
                              <img
                                src={previewImages.c3Photo}
                                alt="Tank Cleaning Preview"
                                className="mx-auto max-h-60 rounded-lg border shadow-sm"
                              />
                            )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-gray-700 font-medium text-sm break-all">
                                {form.c3Photo.name}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({ ...prev, c3Photo: null }));
                                setPreviewImages((prev) => ({
                                  ...prev,
                                  c3Photo: null,
                                }));
                              }}
                              className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint}
                        </p>
                      )}
                    </div>

                    {errors.c3Photo && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c3Photo}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* C4 - Water Quality Testing */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Thermometer size={18} className="mr-2" />
                C4. {t.modules.kitchenCumDining.sections.C.questions.waterTesting}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchenCumDining.sections.C.hints.waterTesting}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {options.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.c4 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.c4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="c4"
                      value={option}
                      checked={form.c4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.c4 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c4}
                </p>
              )}

              {form.c4 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DATE INPUT */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 flex items-center">
                      <CalendarDays size={16} className="mr-2" />
                      Last date of water quality testing:
                    </label>

                    <input
                      type="date"
                      name="c4Date"
                      value={form.c4Date}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      max={new Date().toISOString().split("T")[0]}
                      className={`w-full border p-3 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                        errors.c4Date ? "border-red-500" : "border-gray-300"
                      }`}
                    />

                    {errors.c4Date && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c4Date}
                      </p>
                    )}

                    <p className="text-xs text-gray-500 mt-1">
                      If not entered, section will be marked as "Partially Filled"
                    </p>
                  </div>

                  {/* REPORT UPLOAD */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload water quality test report (Optional):
                    </label>

                    {/* CAMERA + UPLOAD BUTTONS */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c4Report");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c4Report"
                        className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                          isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    {/* HIDDEN INPUT */}
                    <input
                      type="file"
                      name="c4Report"
                      id="c4Report"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW BOX */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                        form.c4Report
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c4Report ? (
                        <div className="space-y-4">
                          {/* ✅ IMAGE PREVIEW (only if image) */}
                          {previewImages.c4Report &&
                            form.c4Report.type.startsWith("image/") && (
                              <img
                                src={previewImages.c4Report}
                                alt="Water Test Report Preview"
                                className="mx-auto max-h-60 rounded-lg border shadow-sm"
                              />
                            )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-gray-700 font-medium text-sm break-all">
                                {form.c4Report.name}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  c4Report: null,
                                }));
                                setPreviewImages((prev) => ({
                                  ...prev,
                                  c4Report: null,
                                }));
                              }}
                              className="text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          Capture or upload test report
                        </p>
                      )}
                    </div>

                    {errors.c4Report && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.c4Report}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for comprehensive water supply assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_c}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              const previewUrl = URL.createObjectURL(file);
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: previewUrl,
              }));
            }

            setShowCamera(false);
            setCameraField(null);
          }}
          onClose={() => {
            setShowCamera(false);
            setCameraField(null);
          }}
        />
      )}
    </div>
  );
};

export default SectionCWaterSupply;